import { api, Settings } from '@/app'

import { getRedirectUri } from '@/features/Auth/session/linkedInRedirect'

/**
 * The API calls authentication makes.
 *
 * They lived among the profile requests because both address the `profiles/` routes, but
 * asking whether an address is free, exchanging a LinkedIn token and requesting a reset are
 * not profile reads — they are steps in signing in, and the providers and account flows here
 * are their only callers. See EPIC-005.
 */
const BASE_URL = 'profiles/'

export const ERRORS = {
  emailInUse: new Error('auth/email-already-in-use'),
  notRegistered: new Error('auth/not-registered'),
  reautenticationRequired: new Error('auth/requires-recent-login'),
  /**
   * The provider could not produce a session, so there is nothing to create a profile for.
   * Carries no message of its own: `useAuthFlow` has already reported the failure, and it is
   * the only place in the feature that shows one.
   */
  credentialFailed: new Error('auth/credential-failed')
}

export async function isEmailAvailable(email: string) {
  const response = await api.get(`${BASE_URL}check_email/`, {
    params: { email: email?.trim?.().toLowerCase?.() ?? '' },
    validateStatus: () => true
  })

  // 204 is free and 409 is taken. `validateStatus` accepts everything so the 409 is not an
  // exception, which means every other status arrives here too — and reading any 2xx as
  // "free" would let an unrelated success answer a question it was not asked.
  return response.status === 204
}

type LinkedinAuthResponse = {
  profile: {
    id: string
    localizedFirstName: string
    localizedLastName: string
    email: string
  }
  token: string
}

export async function linkedInAuth(accessToken: string) {
  const response = await api.get<LinkedinAuthResponse>(`${BASE_URL}linkedin/`, {
    params: { access_token: accessToken },
    // Read by the request middleware, which the client's own config type does not describe.
    noAuth: true
  } as never)

  return response.data
}

type LinkedInAccessTokenResponse = {
  access_token: string
}

const LinkedInCredentials = Settings.ApiCredentials.LinkedIn

/**
 * The authorization code LinkedIn redirects back with, traded for an access token.
 *
 * The same code arrives twice whenever the callback route renders twice, and LinkedIn
 * refuses a code it has already spent. `spentCode` is what makes the second call a no-op
 * rather than an error the person sees.
 */
let spentCode: string | null = null

export async function getLinkedInAccessToken(code: string) {
  if (spentCode === code) return null

  spentCode = code

  try {
    const response = await api.post<LinkedInAccessTokenResponse>(`${BASE_URL}linkedin/access_token/`, {
      grant_type: 'authorization_code',
      code,
      client_id: LinkedInCredentials.ClientID,
      client_secret: LinkedInCredentials.ClientSecret,
      redirect_uri: getRedirectUri()
    })

    return response.data
  } catch (error) {
    spentCode = null

    throw error
  }
}

/**
 * Asks the API to send a reset email. Resolving means the API accepted the request, not
 * that an email went out: the endpoint answers 200 for an address with no profile on
 * purpose, so it cannot be used to enumerate accounts.
 */
export async function passwordReset(forEmail: string) {
  await api.get(BASE_URL + 'request_password_reset/', { params: { email: forEmail } })
}

export async function requestEmailVerification() {
  await api.get(BASE_URL + 'request_email_verification/')
}

export async function getImpersonationToken(credentials: { email: string; password: string }) {
  const response = await api.post<string>(BASE_URL + 'token/', credentials, {
    params: { mode: 'sign_in' }
  })

  return response.data
}
